import { FilterListRounded } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { ToggleButtonSubmenu } from "src/components";
import { UserFeedbackFilterByOption, UserFeedbackGroupByOption } from "src/constants";

interface Props {
  groupBy: UserFeedbackGroupByOption;
  filters: Record<UserFeedbackFilterByOption, string[]>;
  disabled?: boolean;
  onGroupByChanged: (value: UserFeedbackGroupByOption) => void;
  onFilterByChanged: (filters: Record<UserFeedbackFilterByOption, string[]>) => void;
}

const UserFeedbackFilterButton = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByOptionChanged(options: string[]) {
    // Ignore other options that require a value selection from a submenu
    if (options.includes("All")) {
      props.onFilterByChanged({
        [UserFeedbackFilterByOption.Kind]: [],
        [UserFeedbackFilterByOption.Sender]: [],
      });
    }
  }

  function handleFilterByValueChanged(option: UserFeedbackFilterByOption, values: string[]) {
    if (values != null) {
      props.onFilterByChanged({
        ...props.filters,
        [option]: values,
      });
    }
  }

  const appliedFilters = [UserFeedbackFilterByOption.Kind, UserFeedbackFilterByOption.Sender].filter((x) => {
    return props.filters[x].length > 0;
  });

  return (
    <>
      <Badge color="primary" variant="dot" invisible={appliedFilters.length === 0}>
        <Button
          variant="tertiary"
          disabled={props.disabled}
          sx={{ minWidth: 0, p: 0.25 }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <FilterListRounded />
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: { sx: { minWidth: 200 } },
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Group By
        </Typography>
        <ToggleButtonGroup
          value={props.groupBy}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => props.onGroupByChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={UserFeedbackGroupByOption.None}>
            <Typography>None</Typography>
          </ToggleButton>
          <ToggleButton value={UserFeedbackGroupByOption.Kind}>
            <Typography>Kind</Typography>
          </ToggleButton>
          <ToggleButton value={UserFeedbackGroupByOption.Sender}>
            <Typography>Sender</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Filter By
        </Typography>
        <ToggleButtonGroup
          value={appliedFilters.length > 0 ? appliedFilters : ["All"]}
          orientation="vertical"
          variant="menu"
          onChange={(_, values) => handleFilterByOptionChanged(values)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButtonSubmenu
            label="Kind"
            value={UserFeedbackFilterByOption.Kind}
            suboptions={["Performance Feedback", "Self-Reflection", "Peer to Peer Feedback"]}
            selectedSuboptionValues={props.filters[UserFeedbackFilterByOption.Kind]}
            onSelected={(selectedSuboptionValues) =>
              handleFilterByValueChanged(UserFeedbackFilterByOption.Kind, selectedSuboptionValues)
            }
          />

          <ToggleButtonSubmenu
            disabled={true}
            label="Sender"
            value={UserFeedbackFilterByOption.Sender}
            suboptions={[]}
            selectedSuboptionValues={props.filters[UserFeedbackFilterByOption.Sender]}
            onSelected={() => null}
          />
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { UserFeedbackFilterButton };
